/* ══════════════════════════════════════════════════════════════════════════
   ALIA · EMBER — all page choreography, one file.

   Deliberately NOT split per section. The animation is one orchestrated
   timeline: a single Lenis instance drives a single ScrollTrigger registry,
   and the hero, sear, burn and ember canvases are scheduled against each
   other. Fragmenting this into per-section scripts would give every section
   its own scroll listener and its own idea of the timeline.

   It targets the section markup by class and id. Those selectors are the
   contract — renaming one in a section file silently breaks the animation
   with no error.

   Load order matters: gsap, ScrollTrigger, SplitText and lenis must be
   present first. layout/theme.liquid loads all five with defer, which
   guarantees document order.
   ═════════════════════════════════════════════════════════════════════════ */
(function(){
  "use strict";
  var hasGSAP = !!window.gsap;
  var hasST = !!window.ScrollTrigger;
  var hasSplit = !!window.SplitText;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches;
  var isTouch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
  if(isTouch) document.body.classList.add('touch');
  var $ = function(s,c){return (c||document).querySelector(s);};
  var $$ = function(s,c){return Array.prototype.slice.call((c||document).querySelectorAll(s));};
  var clamp = function(v,a,b){return Math.max(a,Math.min(b,v));};
  var lerp = function(a,b,t){return a+(b-a)*t;};

  var E = {
    reveal:'expo.out',
    rise:'power4.out',
    soft:'power3.out',
    light:'power1.inOut',
    inOut:'power2.inOut'
  };

  if(hasGSAP && hasST){
    gsap.registerPlugin(ScrollTrigger);
    if(hasSplit){ try{ gsap.registerPlugin(SplitText); }catch(e){} }
    gsap.defaults({ ease:E.soft });
  }

  /* ───────── CURSOR ───────── */
  function initCursor(){
    if(isTouch) return;
    var cur = $('.cursor'), dot = $('.cursor-dot');
    if(!cur||!dot) return;
    var mx=innerWidth/2,my=innerHeight/2,cx=mx,cy=my;
    window.addEventListener('mousemove', function(e){
      mx=e.clientX; my=e.clientY;
      dot.style.transform='translate('+mx+'px,'+my+'px)';
      if(!cur.classList.contains('ready')){ cur.classList.add('ready'); dot.classList.add('ready'); }
    }, {passive:true});
    function raf(){
      cx += (mx-cx)*0.16; cy += (my-cy)*0.16;
      cur.style.transform='translate('+cx+'px,'+cy+'px)';
      requestAnimationFrame(raf);
    }
    raf();
    document.addEventListener('mouseover', function(e){
      if(e.target.closest && e.target.closest('[data-cursor],a,button')) cur.classList.add('on');
    });
    document.addEventListener('mouseout', function(e){
      if(e.target.closest && e.target.closest('[data-cursor],a,button')) cur.classList.remove('on');
    });
  }

  /* ───────── NAV ───────── */
  function initNav(){
    var nav = $('#nav');
    if(nav){
      var upd = function(){
        var y = window.pageYOffset || document.documentElement.scrollTop;
        if(y>60) nav.classList.add('shrunk'); else nav.classList.remove('shrunk');
      };
      upd();
      window.addEventListener('scroll', upd, {passive:true});
    }
    var burger = $('#burger'), mm = $('#mobileMenu');
    if(burger && mm){
      burger.addEventListener('click', function(){
        var open = mm.classList.toggle('open');
        burger.classList.toggle('x', open);
        burger.setAttribute('aria-expanded', open?'true':'false');
        document.body.classList.toggle('menu-open', open);
      });
      $$('[data-mm]', mm).forEach(function(a){
        a.addEventListener('click', function(){
          mm.classList.remove('open'); burger.classList.remove('x');
          burger.setAttribute('aria-expanded','false');
          document.body.classList.remove('menu-open');
        });
      });
    }

    /* ── mega menu ── hover with a close delay on pointer devices, click and
       Escape everywhere, so it works the same on a trackpad and a keyboard. */
    var mTrig = $('#megaTrigger'), mega = $('#megaPanel');
    if(mTrig && mega){
      var megaOpen = false, megaTimer = null;
      var setMega = function(on){
        megaOpen = on;
        mega.classList.toggle('open', on);
        mega.setAttribute('aria-hidden', on ? 'false' : 'true');
        mTrig.setAttribute('aria-expanded', on ? 'true' : 'false');
        if(nav) nav.classList.toggle('mega-on', on);
      };
      var megaHold = function(){ if(megaTimer){ clearTimeout(megaTimer); megaTimer = null; } };
      var megaLater = function(){ megaHold(); megaTimer = setTimeout(function(){ setMega(false); }, 240); };

      mTrig.addEventListener('click', function(e){ e.preventDefault(); setMega(!megaOpen); });
      if(!isTouch){
        mTrig.addEventListener('mouseenter', function(){ megaHold(); setMega(true); });
        mTrig.addEventListener('mouseleave', megaLater);
        mega.addEventListener('mouseenter', megaHold);
        mega.addEventListener('mouseleave', megaLater);
      }
      mega.addEventListener('click', function(e){
        if(e.target && e.target.closest && e.target.closest('a')) setMega(false);
      });
      document.addEventListener('click', function(e){
        if(!megaOpen) return;
        if(mTrig.contains(e.target) || mega.contains(e.target)) return;
        setMega(false);
      });
      document.addEventListener('keydown', function(e){
        if(e.key === 'Escape' && megaOpen){ setMega(false); mTrig.focus(); }
      });
    }
  }

  /* ───────── SMOOTH ANCHORS ───────── */
  function scrollToEl(sel){
    var el = $(sel); if(!el) return;
    if(window.__lenis && window.__lenis.scrollTo){ window.__lenis.scrollTo(el,{offset:-4}); }
    else { el.scrollIntoView({behavior:reduce?'auto':'smooth'}); }
  }
  function initAnchors(){
    $$('a[href^="#"]').forEach(function(a){
      a.addEventListener('click', function(e){
        var href = a.getAttribute('href');
        if(href && href.length>1 && $(href)){ e.preventDefault(); scrollToEl(href); }
      });
    });
  }

  /* ───────── SCROLL PROGRESS ───────── */
  function initProgress(){
    var bar = $('#progressBar');
    if(!bar) return;
    if(!hasGSAP || !hasST){
      window.addEventListener('scroll', function(){
        var h = document.documentElement;
        var p = h.scrollTop / (h.scrollHeight - h.clientHeight || 1);
        bar.style.transform = 'scaleX('+clamp(p,0,1)+')';
      }, {passive:true});
      return;
    }
    gsap.to(bar, {scaleX:1, ease:'none', scrollTrigger:{trigger:document.body, start:'top top', end:'bottom bottom', scrub:0.3}});
  }

  /* ───────── GENERIC REVEALS ───────── */
  function initReveals(root){
    if(!hasGSAP || !hasST || reduce) return;
    $$('[data-reveal]', root).forEach(function(el){
      if(el.__aliaReveal) return;   /* never bind the same element twice: the
                                       editor calls this again per section */
      el.__aliaReveal = 1;
      gsap.set(el,{opacity:0,y:26});
      ScrollTrigger.create({trigger:el, start:'top 88%', once:true, onEnter:function(){
        gsap.to(el,{opacity:1,y:0,duration:1.0,ease:E.reveal});
      }});
    });
  }

  /* ───────── HERO SUB / GENERIC HERO ELEMENTS handled in heroIntro ───────── */

  /* ───────── HEADLINES (SplitText line-mask rise) ───────── */
  function initHeadlines(){
    if(!hasGSAP || !hasST) return;
    $$('[data-headline]').forEach(function(el){
      if(reduce){ return; }
      var targets, mask;
      if(hasSplit){
        try{
          var sp = new SplitText(el,{type:'lines',linesClass:'sp-ln'});
          sp.lines.forEach(function(l){
            var w = document.createElement('span');
            w.className = 'ln-mask'; w.style.display='block'; w.style.overflow='hidden';
            l.parentNode.insertBefore(w,l); w.appendChild(l);
            l.style.display='block';
          });
          targets = sp.lines;
        }catch(e){ targets = [el]; }
      } else {
        targets = [el];
      }
      gsap.set(targets,{yPercent:118});
      ScrollTrigger.create({trigger:el, start:'top 86%', once:true, onEnter:function(){
        gsap.to(targets,{yPercent:0,duration:1.05,ease:E.rise,stagger:0.08});
      }});
    });
  }

  /* ───────── HERO INTRO + EMBER CANVAS ───────── */
  var heroVel = 0; // shared scroll velocity for embers
  function heroIntro(){
    var h1lines = $$('#hero .hero-h1 .ln');
    var others = $$('#hero [data-hero-sub]');
    var eyebrow = $('#hero .hero-eyebrow .eyebrow');
    if(reduce || !hasGSAP){ return; }
    gsap.set(h1lines,{yPercent:120});
    if(eyebrow) gsap.set(eyebrow,{yPercent:120});
    gsap.set(others,{opacity:0,y:24});
    var tl = gsap.timeline({delay:0.1});
    if(eyebrow) tl.to(eyebrow,{yPercent:0,duration:0.9,ease:E.rise},0);
    tl.to(h1lines,{yPercent:0,duration:1.15,ease:E.rise,stagger:0.12},0.15);
    tl.to(others,{opacity:1,y:0,duration:1.0,ease:E.reveal,stagger:0.1},0.5);
  }

  function initEmber(canvasSel, opts){
    var cv = $(canvasSel);
    if(!cv) return null;
    opts = opts || {};
    var ctx = cv.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio||1, 2);
    var W=0,H=0, parts=[], running=false, raf=0;
    var density = opts.density || 0.00012;
    var baseUp = opts.up || 0.55;
    function size(){
      var r = cv.getBoundingClientRect();
      W = Math.max(1, Math.round(r.width*dpr));
      H = Math.max(1, Math.round(r.height*dpr));
      cv.width=W; cv.height=H;
      seed();
    }
    function mk(initial){
      var x = Math.random()*W;
      var y = initial ? Math.random()*H : H + Math.random()*40*dpr;
      return {
        x:x, y:y,
        r:(Math.random()*2.0+0.6)*dpr,
        vy:-(Math.random()*baseUp+0.25)*dpr,
        drift:(Math.random()*0.6-0.3),
        ph:Math.random()*Math.PI*2,
        sp:Math.random()*0.02+0.008,
        life:0, max:Math.random()*260+160,
        hot:Math.random()
      };
    }
    function seed(){
      var target = Math.round(W*H*density);
      target = clamp(target, 24, 190);
      parts = [];
      for(var i=0;i<target;i++) parts.push(mk(true));
    }
    function step(){
      ctx.clearRect(0,0,W,H);
      ctx.globalCompositeOperation = 'lighter';
      var wind = heroVel*0.9;
      for(var i=0;i<parts.length;i++){
        var p = parts[i];
        p.ph += p.sp;
        p.x += (p.drift + Math.sin(p.ph)*0.5 + wind)*dpr;
        p.y += p.vy;
        p.vy *= 0.995;
        p.life++;
        var lifeT = p.life/p.max;
        var a = Math.sin(Math.min(lifeT,1)*Math.PI); // fade in and out
        a *= (0.5 + Math.sin(p.ph*3)*0.28);
        a = clamp(a,0,1);
        if(p.y < -20*dpr || p.life > p.max || p.x < -40*dpr || p.x > W+40*dpr){
          parts[i] = mk(false); continue;
        }
        var rad = p.r*(1.2 - lifeT*0.4);
        // amber glow
        var g = ctx.createRadialGradient(p.x,p.y,0,p.x,p.y,rad*4.2);
        var core = p.hot>0.55 ? '255,238,214' : '' + Math.round(lerp(200,255,p.hot)) + ',' + Math.round(lerp(150,210,p.hot)) + ',' + Math.round(lerp(90,150,p.hot));
        g.addColorStop(0,'rgba('+core+','+(a*0.95)+')');
        g.addColorStop(0.35,'rgba(138,95,58,'+(a*0.55)+')');
        g.addColorStop(1,'rgba(138,95,58,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x,p.y,rad*4.2,0,Math.PI*2);
        ctx.fill();
        // hot core dot
        ctx.fillStyle = 'rgba(255,244,228,'+(a*0.9)+')';
        ctx.beginPath();
        ctx.arc(p.x,p.y,Math.max(0.4,rad*0.5),0,Math.PI*2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';
      heroVel *= 0.92; // decay velocity
    }
    function loop(){ if(!running) return; step(); raf=requestAnimationFrame(loop); }
    function start(){ if(running) return; running=true; raf=requestAnimationFrame(loop); }
    function stop(){ running=false; if(raf) cancelAnimationFrame(raf); }
    size();
    if(reduce){ step(); return {resize:size}; }
    var winObserve = opts.observe || cv;
    if('IntersectionObserver' in window){
      var io = new IntersectionObserver(function(en){
        en.forEach(function(e){ if(e.isIntersecting) start(); else stop(); });
      }, {threshold:0.02});
      io.observe(winObserve);
    } else { start(); }
    var rt;
    window.addEventListener('resize', function(){ clearTimeout(rt); rt=setTimeout(size,180); }, {passive:true});
    return {resize:size, start:start, stop:stop};
  }

  /* feed scroll velocity into embers */
  function initHeroVelocity(){
    var last = window.pageYOffset||0, lastT = performance.now();
    window.addEventListener('scroll', function(){
      var y = window.pageYOffset||0, t = performance.now();
      var dt = Math.max(16, t-lastT);
      var v = (y-last)/dt; // px per ms
      heroVel += clamp(v*0.5, -1.4, 1.4);
      heroVel = clamp(heroVel, -3, 3);
      last=y; lastT=t;
    }, {passive:true});
  }

  /* ───────── HERO LIVE READOUT ───────── */
  function initHeroTemp(){
    var el = $('#heroTemp');
    if(!el || reduce) return;
    var base = 592, running=true;
    var hero = $('#hero');
    if('IntersectionObserver' in window && hero){
      var io = new IntersectionObserver(function(en){ en.forEach(function(e){ running=e.isIntersecting; }); },{threshold:0.05});
      io.observe(hero);
    }
    setInterval(function(){
      if(!running) return;
      var flick = base + Math.round(Math.sin(performance.now()*0.001)*22 + (Math.random()*18-9));
      el.textContent = flick + '°C';
    }, 480);
  }

  /* ───────── BURN REVEALS ───────── */
  function initBurn(){
    if(!hasGSAP || !hasST || reduce) return;
    $$('[data-burn]').forEach(function(card){
      var burn = $('.burn', card), edge = $('.burn-edge', card), media = $('.dish-media', card);
      if(!burn || !media) return;
      gsap.set(burn,{opacity:1,yPercent:0});
      if(edge) gsap.set(edge,{opacity:0,top:'100%'});
      ScrollTrigger.create({trigger:card, start:'top 82%', once:true, onEnter:function(){
        var tl = gsap.timeline();
        if(edge){
          tl.set(edge,{opacity:1,top:'100%'})
            .to(edge,{top:'-10%',duration:1.15,ease:'power2.in'},0)
            .to(edge,{opacity:0,duration:0.3},0.95);
        }
        tl.to(burn,{yPercent:-104,duration:1.2,ease:'power3.inOut'},0.02)
          .set(burn,{opacity:0});
        gsap.fromTo($('.disc',card)||media,{scale:1.12},{scale:1,duration:1.4,ease:E.reveal},0.1);
      }});
    });
  }

  /* ───────── SMOKE WISPS (char section) ───────── */
  function initSmoke(){
    var cv = $('#charWisp');
    if(!cv || reduce) return;
    var ctx = cv.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio||1, 2);
    var W=0,H=0, wisps=[], running=false, raf=0;
    function size(){
      var r = cv.getBoundingClientRect();
      W=Math.max(1,Math.round(r.width*dpr)); H=Math.max(1,Math.round(r.height*dpr));
      cv.width=W; cv.height=H; make();
    }
    function make(){
      wisps=[];
      var n = clamp(Math.round(W/ (260*dpr)), 3, 8);
      for(var i=0;i<n;i++){
        wisps.push({x:Math.random()*W, y:H*(0.4+Math.random()*0.6), r:(Math.random()*90+70)*dpr, vy:-(Math.random()*0.25+0.1)*dpr, drift:Math.random()*0.3-0.15, a:Math.random()*0.05+0.02, ph:Math.random()*6.28, sp:Math.random()*0.006+0.002});
      }
    }
    function step(){
      ctx.clearRect(0,0,W,H);
      for(var i=0;i<wisps.length;i++){
        var w=wisps[i];
        w.ph+=w.sp; w.y+=w.vy; w.x+=w.drift + Math.sin(w.ph)*0.3;
        if(w.y < -w.r){ w.y = H + w.r*0.5; w.x = Math.random()*W; }
        var g = ctx.createRadialGradient(w.x,w.y,0,w.x,w.y,w.r);
        g.addColorStop(0,'rgba(138,95,58,'+w.a+')');
        g.addColorStop(1,'rgba(138,95,58,0)');
        ctx.fillStyle=g;
        ctx.beginPath(); ctx.arc(w.x,w.y,w.r,0,6.283); ctx.fill();
      }
    }
    function loop(){ if(!running) return; step(); raf=requestAnimationFrame(loop); }
    function start(){ if(running)return; running=true; raf=requestAnimationFrame(loop); }
    function stop(){ running=false; if(raf) cancelAnimationFrame(raf); }
    size();
    var sec = $('#char');
    if('IntersectionObserver' in window && sec){
      var io=new IntersectionObserver(function(en){ en.forEach(function(e){ if(e.isIntersecting) start(); else stop(); }); },{threshold:0.02});
      io.observe(sec);
    } else start();
    var rt; window.addEventListener('resize', function(){ clearTimeout(rt); rt=setTimeout(size,180); }, {passive:true});
  }

  /* ───────── THE SEAR (SET PIECE) ───────── */
  /* ───────── THE SEAR ─────────
     One custom property drives everything. setProgress writes --sear (0..1)
     onto the stage and each visual state — grate heat, crust colour, char
     lines, glow, embers — is a CSS expression of it.

     This replaces a canvas that measured its own pixel size once, at boot. If
     that measurement landed while the element had no layout box, width and
     height clamped to 1 and it drew into a 1x1 buffer for the rest of the
     session with no way back. An SVG with a viewBox has no pixel size of its
     own to get wrong — it scales to whatever box it lands in, whenever that
     box appears. The readouts never broke because they were plain DOM; now the
     picture works the same way. */
  function initSear(){
    var stage = $('.sear-inner');
    if(!stage) return;

    /* Where CSS scroll timelines exist, the section drives ITSELF — picture and
       readouts — from one keyframe set in 01-ember.css, with no dependency on
       GSAP, ScrollTrigger, or this function even being reached.

       Standing down here is not optional: the readouts are printed by CSS
       counters in ::after, so if this also wrote textContent you would see the
       number twice. Everything below is the fallback for browsers without it. */
    if(window.CSS && CSS.supports && CSS.supports('animation-timeline','view()')) return;

    var elTemp=$('#searTemp'), elMai=$('#searMaillard'), elTime=$('#searTime'), elChar=$('#searChar');
    var progress = -1;

    function setProgress(p){
      p = clamp(p,0,1);
      if(Math.abs(p - progress) < 0.002) return;   /* scrub fires constantly */
      progress = p;
      stage.style.setProperty('--sear', p.toFixed(3));
      if(elTemp) elTemp.textContent = Math.round(lerp(24,232,p));
      if(elMai)  elMai.textContent  = Math.round(p*100);
      if(elChar) elChar.textContent = Math.round(clamp(p*1.05,0,1)*100);
      if(elTime){
        var secs = Math.round(p*225), m = Math.floor(secs/60), s = secs%60;
        elTime.textContent = m + ':' + (s<10?'0':'') + s;
      }
    }

    setProgress(0);

    /* no scroll engine: show it mid-sear rather than raw, so the section still
       reads as the finished thing */
    if(reduce || !hasGSAP || !hasST){ setProgress(0.72); return; }

    /* ONE trigger, no pin, every width.

       The desktop path used to pin .sear-stage and scrub a 1500px timeline
       over it. The pin stopped engaging after the page was split into sections
       — onUpdate never fired, --sear stayed at 0, and the cut sat raw on a cold
       grate however far you scrolled. Everything else on the page uses plain
       start/end triggers and they all work, including the sear's own mobile
       branch, so the pin was the one thing not earning its keep.

       This scrubs across the section's own height instead: the cut starts raw
       as the section rises into view and is fully seared by the time it
       leaves. Same choreography, no pinning to go wrong. */
    ScrollTrigger.create({
      trigger: '#sear',
      start: 'top 85%',
      end: 'bottom 45%',
      scrub: 0.4,
      onUpdate: function(self){ setProgress(self.progress); },
      onRefresh: function(self){ setProgress(self.progress); }
    });
  }

  /* ───────── COUNTDOWN TO 7PM DOORS ───────── */
  function initCountdown(){
    var DOORS_DAYS = [4,5,6]; // Thu, Fri, Sat
    var elD=$('#cdD'), elH=$('#cdH'), elM=$('#cdM'), elS=$('#cdS'), elDoors=$('#cdDoors'), elStatus=$('#cdStatus');
    if(!elD) return;
    var DAY_NAMES=['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    function windowEnd(g){ return g===4 ? 1 : 2; } // Thu closes 1AM, Fri/Sat 2AM
    function build(now){
      var openNow=null, next=null;
      for(var i=-1;i<10;i++){
        var d = new Date(now.getFullYear(), now.getMonth(), now.getDate()+i, 0,0,0,0);
        var g = d.getDay();
        if(DOORS_DAYS.indexOf(g) < 0) continue;
        var start = new Date(d); start.setHours(19,0,0,0);
        var end = new Date(d); end.setDate(end.getDate()+1); end.setHours(windowEnd(g),0,0,0);
        if(now >= start && now < end) openNow = {start:start,end:end,g:g};
        if(start > now && !next) next = {start:start,end:end,g:g};
      }
      return {openNow:openNow, next:next};
    }
    function two(n){ return (n<10?'0':'')+n; }
    function tick(){
      var now = new Date();
      var r = build(now);
      var target, label, doorsTxt;
      if(r.openNow){
        target = r.openNow.end;
        label = 'Doors are open';
        doorsTxt = 'In service now &middot; until close';
        elStatus.textContent = 'In service';
      } else if(r.next){
        target = r.next.start;
        label = 'Next doors';
        doorsTxt = DAY_NAMES[r.next.g] + ' &middot; 7:00 PM';
        elStatus.textContent = 'Next doors';
      } else {
        return;
      }
      if(elDoors) elDoors.innerHTML = doorsTxt;
      var diff = Math.max(0, target - now);
      var s = Math.floor(diff/1000);
      var days = Math.floor(s/86400); s-=days*86400;
      var hrs = Math.floor(s/3600); s-=hrs*3600;
      var mins = Math.floor(s/60); s-=mins*60;
      elD.textContent = two(days); elH.textContent = two(hrs); elM.textContent = two(mins); elS.textContent = two(s);
    }
    tick();
    setInterval(tick, 1000);
  }

  /* ───────── FAQ ───────── */
  /* ── Day-events gallery: "see more" + click-to-enlarge ──────────────────
     The markup ships with every photo visible and the button hidden, so a
     browser that never gets here still shows the whole gallery. This does the
     reverse: collapse the overflow, then reveal the button.

     The lightbox is one element built on first use and reused for every photo,
     rather than one per tile — with 24 photos that is 23 nodes not created. */
  var corpLb = null;

  function buildCorpLightbox(){
    if(corpLb) return corpLb;

    var el = document.createElement('div');
    el.className = 'corp-lb';
    el.setAttribute('role','dialog');
    el.setAttribute('aria-modal','true');
    el.setAttribute('aria-label','Photo viewer');
    el.hidden = false;
    el.innerHTML =
      '<span class="corp-lb-count" data-lb-count></span>' +
      '<button type="button" class="corp-lb-btn corp-lb-close" data-lb-close aria-label="Close">&#10005;</button>' +
      '<button type="button" class="corp-lb-btn corp-lb-prev" data-lb-prev aria-label="Previous photo">&#8249;</button>' +
      '<button type="button" class="corp-lb-btn corp-lb-next" data-lb-next aria-label="Next photo">&#8250;</button>' +
      '<img class="corp-lb-img" data-lb-img alt="">' +
      '<p class="corp-lb-cap" data-lb-cap></p>';
    document.body.appendChild(el);

    var imgEl   = $('[data-lb-img]', el);
    var capEl   = $('[data-lb-cap]', el);
    var countEl = $('[data-lb-count]', el);

    var shots = [];     /* the buttons of the gallery currently being viewed */
    var index = 0;
    var lastFocus = null;

    function show(i){
      if(!shots.length) return;
      /* wrap, so next from the last goes back to the first */
      index = (i + shots.length) % shots.length;
      var btn = shots[index];
      var cap = btn.getAttribute('data-cap') || '';
      imgEl.src = btn.getAttribute('data-full');
      imgEl.alt = cap;
      capEl.textContent = cap;
      capEl.hidden = !cap;
      countEl.textContent = (index + 1) + ' / ' + shots.length;
      /* one photo: the arrows would be dead controls */
      $('[data-lb-prev]', el).hidden = shots.length < 2;
      $('[data-lb-next]', el).hidden = shots.length < 2;
    }

    function lock(on){
      document.documentElement.style.overflow = on ? 'hidden' : '';
      document.body.style.overflow = on ? 'hidden' : '';
      /* overflow:hidden alone does not stop Lenis — it drives scroll itself */
      var l = window.__lenis;
      if(l){ if(on){ if(l.stop) l.stop(); } else if(l.start) l.start(); }
      /* the widget's "?" bubble sits at the top of the stacking order and
         would otherwise float over a full-screen photo */
      var host = document.getElementById('alia-wgt-host');
      if(host) host.style.visibility = on ? 'hidden' : '';
    }

    function open(list, i, trigger){
      shots = list;
      lastFocus = trigger || document.activeElement;
      show(i);
      el.classList.add('open');
      lock(true);
      $('[data-lb-close]', el).focus();
    }

    function close(){
      el.classList.remove('open');
      lock(false);
      /* hand focus back to the tile they came from, not the top of the page */
      if(lastFocus && lastFocus.focus) lastFocus.focus();
      lastFocus = null;
    }

    el.addEventListener('click', function(ev){
      if(ev.target.closest('[data-lb-close]')) return close();
      if(ev.target.closest('[data-lb-prev]'))  return show(index - 1);
      if(ev.target.closest('[data-lb-next]'))  return show(index + 1);
      /* a click on the backdrop closes; on the photo itself it must not */
      if(ev.target === el) close();
    });

    document.addEventListener('keydown', function(ev){
      if(!el.classList.contains('open')) return;
      if(ev.key === 'Escape')     { ev.preventDefault(); close(); }
      else if(ev.key === 'ArrowLeft')  { ev.preventDefault(); show(index - 1); }
      else if(ev.key === 'ArrowRight') { ev.preventDefault(); show(index + 1); }
      else if(ev.key === 'Tab'){
        /* keep Tab inside the dialog while it is open */
        var f = $$('button:not([hidden])', el);
        if(!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if(ev.shiftKey && document.activeElement === first){ ev.preventDefault(); last.focus(); }
        else if(!ev.shiftKey && document.activeElement === last){ ev.preventDefault(); first.focus(); }
      }
    });

    corpLb = { open: open, close: close };
    return corpLb;
  }

  function initCorpGallery(root){
    $$('[data-corp-gal]', root).forEach(function(gal){
      if(gal.__corpInit) return;
      gal.__corpInit = 1;

      var shots   = $$('.corp-shot', gal);
      var initial = parseInt(gal.getAttribute('data-initial'), 10) || 6;
      var wrap    = gal.parentNode;
      var btn     = wrap ? $('[data-corp-more]', wrap) : null;

      /* ── see more ── */
      if(btn && shots.length > initial){
        var expanded = false;

        var apply = function(){
          shots.forEach(function(s, i){ s.hidden = !expanded && i >= initial; });
          btn.textContent = expanded
            ? btn.getAttribute('data-label-less')
            : btn.getAttribute('data-label-more');
          btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        };

        apply();
        btn.hidden = false;

        btn.addEventListener('click', function(){
          expanded = !expanded;
          apply();
          /* collapsing from far down the gallery would strand the viewport
             below the section, so come back to the button */
          if(!expanded){
            var top = gal.getBoundingClientRect().top + window.pageYOffset - 120;
            if(window.__lenis && window.__lenis.scrollTo) window.__lenis.scrollTo(top);
            else window.scrollTo({ top: top, behavior: reduce ? 'auto' : 'smooth' });
          }
          if(hasST) ScrollTrigger.refresh();
        });
      }

      /* ── click to enlarge ── */
      if(gal.getAttribute('data-lightbox') === 'false') return;
      var openers = $$('.corp-open', gal);
      if(!openers.length) return;

      openers.forEach(function(op, i){
        op.addEventListener('click', function(){
          /* only offer what is on screen — paging into hidden photos would
             contradict the button they have not pressed yet */
          var visible = openers.filter(function(o){
            var fig = o.closest('.corp-shot');
            return fig && !fig.hidden;
          });
          buildCorpLightbox().open(visible, visible.indexOf(op), op);
        });
      });
    });
  }

  function initFaq(){
    $$('.faq-item').forEach(function(item){
      var q = $('.faq-q', item), a = $('.faq-a', item), inner = $('.faq-a-inner', item);
      if(!q||!a) return;
      q.addEventListener('click', function(){
        var open = item.classList.contains('open');
        $$('.faq-item.open').forEach(function(o){
          if(o!==item){
            o.classList.remove('open');
            var oa=$('.faq-a',o), oq=$('.faq-q',o);
            if(oq) oq.setAttribute('aria-expanded','false');
            if(hasGSAP && !reduce) gsap.to(oa,{height:0,duration:0.4,ease:E.inOut}); else oa.style.height='0px';
          }
        });
        if(open){
          item.classList.remove('open');
          q.setAttribute('aria-expanded','false');
          if(hasGSAP && !reduce) gsap.to(a,{height:0,duration:0.4,ease:E.inOut}); else a.style.height='0px';
        } else {
          item.classList.add('open');
          q.setAttribute('aria-expanded','true');
          var h = inner.offsetHeight;
          if(hasGSAP && !reduce) gsap.fromTo(a,{height:a.offsetHeight},{height:h,duration:0.5,ease:E.rise, onComplete:function(){ a.style.height='auto'; }});
          else a.style.height='auto';
        }
      });
    });
  }

  /* ───────── FOOTER WORDMARK EMBER LIGHT ───────── */
  function initFooterWord(){
    var fw = $('#footWord');
    if(!fw) return;
    var text = fw.textContent.trim();
    fw.innerHTML='';
    text.split('').forEach(function(c){
      var s=document.createElement('span'); s.className='ch'; s.textContent=c; fw.appendChild(s);
    });
    var chars = $$('.ch', fw);
    if(!hasGSAP || !hasST || reduce){ chars.forEach(function(c){ c.style.color='var(--bone)'; }); return; }
    ScrollTrigger.create({trigger:'#footer', start:'top 70%', once:true, onEnter:function(){
      gsap.to(chars,{color:'#EEEDE3', textShadow:'0 0 26px rgba(138,95,58,0.6)', duration:1.1, ease:E.light, stagger:0.14});
    }});
    // hover relight
    chars.forEach(function(c){
      c.addEventListener('mouseenter', function(){
        gsap.fromTo(c,{textShadow:'0 0 40px rgba(138,95,58,0.95)'},{textShadow:'0 0 26px rgba(138,95,58,0.6)',duration:0.9,ease:E.soft});
      });
    });
  }

  /* ───────── EASTER EGG: double-click hero flares the embers ───────── */
  function initEgg(){
    var hero = $('#hero');
    if(!hero) return;
    hero.addEventListener('dblclick', function(){
      heroVel += 2.6;
      heroVel = clamp(heroVel, -3, 3);
      if(hasGSAP && !reduce){
        gsap.fromTo('.hero-glow',{opacity:1.4},{opacity:1,duration:1.4,ease:E.light});
      }
    });
  }

  /* ───────── PARALLAX (subtle) ───────── */
  function initParallax(){
    if(!hasGSAP || !hasST || reduce) return;
    gsap.to('.hero-glow',{yPercent:18, ease:'none', scrollTrigger:{trigger:'#hero', start:'top top', end:'bottom top', scrub:true}});
  }

  /* ───────── BOOT ───────── */
  function boot(){
    if(window.Lenis && hasGSAP && hasST && !reduce && !isTouch){
      try{
        var lenis = new Lenis({ lerp:0.1, wheelMultiplier:1, smoothWheel:true });
        lenis.on('scroll', function(){ ScrollTrigger.update(); });
        gsap.ticker.add(function(t){ lenis.raf(t*1000); });
        gsap.ticker.lagSmoothing(0);
        window.__lenis = lenis;
      }catch(e){}
    }
      /* Each init is isolated. They used to run as one straight sequence, so
         the FIRST one to throw silently killed every one after it — which is
         how a single upstream fault took out the FAQ accordion, the countdown
         and the footer wordmark together, with nothing on screen to say why.
         The console line names the culprit instead of leaving it invisible. */
      function safe(name, fn){
        try{ fn(); }
        catch(err){ if(window.console && console.error) console.error("[ALIA] " + name + " failed:", err); }
      }
      safe('initCursor', initCursor);
      safe('initNav', initNav);
      safe('initAnchors', initAnchors);
      safe('initProgress', initProgress);
      safe('initHeroVelocity', initHeroVelocity);
      safe('initEmber:hero',  function(){ initEmber('#emberCanvas', {density:0.00012, up:0.6, observe:$('#hero')}); });
      safe('initEmber:night', function(){ initEmber('#nightEmbers', {density:0.00009, up:0.45, observe:$('#night')}); });
      safe('initEmber:foot',  function(){ initEmber('#footEmbers', {density:0.00008, up:0.4, observe:$('#footer')}); });
      safe('heroIntro', heroIntro);
      safe('initHeadlines', initHeadlines);
      safe('initReveals', function(){ initReveals(); });
      safe('initBurn', initBurn);
      safe('initSmoke', initSmoke);
      safe('initSear', initSear);
      safe('initCountdown', initCountdown);
      safe('initFaq', initFaq);
      safe('initCorpGallery', function(){ initCorpGallery(); });
      safe('initFooterWord', initFooterWord);
      safe('initHeroTemp', initHeroTemp);
      safe('initEgg', initEgg);
      safe('initParallax', initParallax);
    if(hasST){
      ScrollTrigger.refresh();
      if(document.fonts && document.fonts.ready){ document.fonts.ready.then(function(){ ScrollTrigger.refresh(); }); }
      setTimeout(function(){ ScrollTrigger.refresh(); }, 700);
    }
  }

  window.addEventListener('load', function(){ boot(); });

  /* ═══ SHOPIFY THEME EDITOR ═══════════════════════════════════════════════
     boot() runs once, on load. The editor then injects, moves and re-renders
     sections underneath it, so any ScrollTrigger measured against the old
     layout is stale, and anything inside brand-new markup was never bound.

     The choreography stays GLOBAL — one Lenis, one trigger registry. This does
     not re-boot per section: it binds what arrived, drops what left, and
     refreshes the registry once the DOM has settled. */
  var stTimer = null;
  function editorRefresh(){
    if(!hasST) return;
    clearTimeout(stTimer);
    stTimer = setTimeout(function(){ ScrollTrigger.refresh(); }, 60);
  }

  document.addEventListener('shopify:section:load', function(e){
    initReveals(e.target);            /* guarded, so this cannot double-bind */
    initCorpGallery(e.target);        /* same guard, via gal.__corpInit */
    editorRefresh();
  });

  document.addEventListener('shopify:section:unload', function(e){
    if(hasST){
      ScrollTrigger.getAll().forEach(function(st){
        /* a trigger left pointing at removed markup keeps measuring a ghost */
        if(st.trigger && e.target.contains(st.trigger)) st.kill();
      });
    }
    editorRefresh();
  });

  document.addEventListener('shopify:section:reorder', editorRefresh);
  document.addEventListener('shopify:section:select',  editorRefresh);
})();

/* ── sticky Reserve/Call dock reveal (was inline at the foot of the page) ── */
  (function(){
    function run(){
      var d=document.querySelector('.alia-cta-dock'); if(!d) return;
      function reveal(){ d.classList.add('revealed'); }
      var SEL='#preloader,#intro,#introStatement,[id*="preload" i],[class*="preloader" i],[class*="intro-idx"],[class*="intro-col"],[class*="intro-grid"],.intro-statement,.d-veil,.hero-veil,.tex-veil,.pre-veil,[class*="veil"]';
      var pre=document.querySelector(SEL);
      if(pre){
        var start=Date.now();
        var iv=setInterval(function(){
          var el=document.querySelector(SEL), gone=!el;
          if(el){ var cs=getComputedStyle(el); gone=(cs.display==='none'||cs.visibility==='hidden'||parseFloat(cs.opacity)<0.1); }
          if(gone || (Date.now()-start)>6000){ reveal(); clearInterval(iv); }
        },200);
      } else {
        if(document.readyState==='complete'){ setTimeout(reveal,700); }
        else { window.addEventListener('load',function(){ setTimeout(reveal,700); }); }
      }
    }
    if(document.readyState==='loading'){ document.addEventListener('DOMContentLoaded',run); } else { run(); }
  })();
